// Cache priming + background synchronization of offline scans.
import { ApiService } from './api';
import { isOnline } from '../hooks/useOnlineStatus';
import {
  mergeRegistrations,
  pruneEventRegistrations,
  getMeta,
  setMeta,
  listPendingScans,
  deletePendingScan,
  countPendingScans,
  countEventRegistrations,
  listPendingRegistrations,
  deletePendingRegistration,
  addRejected,
  deleteCachedRegistration,
} from './offlineDb';
import { notifyQueueChange } from './offlineOnSpot';

const SNAPSHOT_PAGE_SIZE = 1000;
// Deltas only add/update rows; a periodic full pass also drops deleted registrations.
const FULL_REFRESH_MS = 30 * 60 * 1000;
export const REFRESH_INTERVAL_MS = 60 * 1000;

// ---- tiny pub/sub so the UI can reflect sync/cache state -------------------
const listeners = new Set();
export function onOfflineChange(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}
function emit(evt) {
  listeners.forEach((cb) => {
    try { cb(evt); } catch (_) { /* ignore listener errors */ }
  });
}

function isNetworkError(err) {
  if (!err) return false;
  // Backend down behind the proxy: keep the queued scan instead of dropping it.
  if (err.serverUnavailable) return true;
  if (err.name === 'AbortError' || err.name === 'TypeError') return true;
  return /Failed to fetch|NetworkError|Load failed|network/i.test(err.message || '');
}

async function loadEventConfig(eventId) {
  let purposes = [];
  try { purposes = await ApiService.getQRScanPurposes(eventId); } catch (_) { purposes = []; }

  let formSchema = [];
  try {
    const pub = await ApiService.getPublicEvent(eventId);
    formSchema = pub?.formSchema || [];
  } catch (_) { formSchema = []; }

  // Warm the badge layouts so the print modal works offline.
  const badgePurposes = new Set(['badge', ...purposes.filter((p) => p.shouldPrintBadge).map((p) => p.purpose)]);
  await Promise.all([...badgePurposes].map((purpose) =>
    ApiService.getBadgeDesignerConfig(eventId, purpose).catch(() => null)));
  return { purposes, formSchema };
}

const priming = new Set();

// Snapshot endpoint is off when the backend's offline-sync flag is disabled: fall back to the
// staff-only registrations list (full reload each time; no kiosk support).
async function fetchRegistrationsLegacy(eventId) {
  const rows = [];
  let page = 0;
  let totalPages = 1;
  do {
    const res = await ApiService.getRegistrations(eventId, page, SNAPSHOT_PAGE_SIZE);
    rows.push(...(res?.content || []));
    totalPages = res?.totalPages ?? 1;
    page += 1;
  } while (page < totalPages);
  return rows;
}

/**
 * Fetch everything needed for offline scanning/printing and store it locally.
 * Uses the offline snapshot endpoint, so public kiosks (offline-enabled public desk) work without login.
 * After the first full load only rows changed since the last server time are fetched.
 */
export async function primeScanCache(eventId, { full = false } = {}) {
  if (!eventId || !isOnline() || priming.has(eventId)) return { primed: false };
  priming.add(eventId);
  emit({ type: 'prime:start', eventId });
  try {
    const meta = (await getMeta(eventId)) || {};
    const fullRefresh = full || !meta.serverTime || !meta.fullAt
      || Date.now() - new Date(meta.fullAt).getTime() > FULL_REFRESH_MS;
    const config = fullRefresh || !meta.purposes ? await loadEventConfig(eventId) : {};

    const seen = new Set();
    let serverTime = null;
    let page = 0;
    let hasMore = true;
    let legacy = false;
    while (hasMore) {
      let res;
      try {
        res = await ApiService.getOfflineSnapshot(eventId, {
          since: fullRefresh ? undefined : meta.serverTime,
          page,
          size: SNAPSHOT_PAGE_SIZE,
        });
      } catch (err) {
        if (page > 0 || isNetworkError(err)) throw err;
        legacy = true;
        break;
      }
      const rows = res?.registrations || [];
      serverTime = serverTime || res?.serverTime;
      rows.forEach((row) => seen.add(row.userId));
      await mergeRegistrations(eventId, rows);
      hasMore = !!res?.hasMore;
      page += 1;
    }
    if (legacy) {
      const rows = await fetchRegistrationsLegacy(eventId);
      rows.forEach((row) => seen.add(row.userId));
      await mergeRegistrations(eventId, rows);
    }
    if (fullRefresh || legacy) await pruneEventRegistrations(eventId, seen);

    const now = new Date().toISOString();
    await setMeta(eventId, {
      ...meta,
      ...config,
      serverTime: serverTime || meta.serverTime,
      fullAt: fullRefresh ? now : meta.fullAt,
      syncedAt: now,
    });
    const cached = await countEventRegistrations(eventId);
    emit({ type: 'prime:done', eventId, count: cached, changed: seen.size });
    return { primed: true, count: cached };
  } catch (err) {
    emit({ type: 'prime:error', eventId, error: err?.message });
    return { primed: false, error: err?.message };
  } finally {
    priming.delete(eventId);
  }
}

// ---- background sync -------------------------------------------------------
let syncing = false;
let syncingRegistrations = false;

// Signed out / token expired: keep the item and try again after the desk signs in.
const needsSignIn = (err) => !!err?.authRequired || (err?.status === 403 && /login/i.test(err?.message || ''));

/**
 * Upload on-spot registrations saved offline, oldest first, before any scans (a scan may be for one of them).
 * The server returns the existing registration for an ID it already has, so a repeat upload is harmless.
 */
export async function syncPendingRegistrations() {
  if (syncingRegistrations || !isOnline()) return { synced: 0, blocked: false };
  syncingRegistrations = true;
  let synced = 0;
  let blocked = false;
  try {
    for (const item of await listPendingRegistrations()) {
      try {
        await ApiService.registerUserPublic(item.eventId, item.payload, { withAuth: true, throwOnUnauthorized: true });
        await deletePendingRegistration(item.id);
        synced += 1;
      } catch (err) {
        if (isNetworkError(err) || needsSignIn(err)) {
          blocked = true;
          emit({ type: 'sync:blocked', reason: needsSignIn(err) ? 'signin' : 'network' });
          break;
        }
        await addRejected({
          type: 'registration',
          eventId: item.eventId,
          userId: item.userId,
          name: item.name,
          error: err?.message || 'Server refused the registration',
          payload: item.payload,
          createdAt: item.createdAt,
        });
        await deletePendingRegistration(item.id);
        // Not on the server, so this device shouldn't keep accepting scans for it.
        await deleteCachedRegistration(item.eventId, item.userId);
      }
    }
  } finally {
    syncingRegistrations = false;
    notifyQueueChange();
  }
  return { synced, blocked };
}

/**
 * Replay pending offline scans to the backend in original order.
 * - Preserves order (ascending id); stops on the first network error.
 * - Any backend HTTP response (incl. 409 already-redeemed) is treated as
 *   authoritatively processed and the pending item is removed.
 * - Marked done only after the backend responds (no optimistic deletion).
 * - clientScanId makes a replay after a lost ack a no-op on the server; scannedAt keeps the
 *   original scan time.
 */
export async function syncPendingScans() {
  if (syncing || !isOnline()) return { synced: 0 };
  const registrations = await syncPendingRegistrations();
  if (registrations.blocked) return { synced: 0 };
  syncing = true;
  let synced = 0;
  try {
    const pending = (await listPendingScans()).filter((s) => s.status !== 'synced');
    for (const scan of pending) {
      try {
        await ApiService.scanQRNetwork(scan.eventId, {
          qrData: scan.userId,
          purpose: scan.purpose,
          scanMode: 'manual',
          clientScanId: scan.clientScanId,
          scannedAt: scan.createdAt,
          scannerName: scan.scannerName || '',
        });
        await deletePendingScan(scan.id);
        synced += 1;
        emit({ type: 'sync:item', eventId: scan.eventId });
      } catch (err) {
        if (isNetworkError(err)) {
          // Still offline / dropped mid-flight: keep order, retry later.
          break;
        }
        // The server refused it (already redeemed elsewhere, not found, blocked). Retrying won't change that,
        // so it leaves the queue but stays visible for staff to review.
        await addRejected({
          type: 'scan',
          eventId: scan.eventId,
          userId: scan.userId,
          purpose: scan.purpose,
          error: err?.message || 'Server refused the scan',
          createdAt: scan.createdAt,
          scannerName: scan.scannerName,
        });
        await deletePendingScan(scan.id);
        synced += 1;
      }
    }
  } finally {
    syncing = false;
  }
  const remaining = await countPendingScans();
  emit({ type: 'sync:done', synced, remaining });
  return { synced, remaining };
}

let initialized = false;
/** Register listeners that flush the pending queue whenever we regain network. */
export function initOfflineSync() {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;
  window.addEventListener('online', () => { syncPendingScans(); });
  // Also flush on load in case the app was reopened while online.
  if (isOnline()) syncPendingScans();
}
